package com.dotdrives.web.pages.nextsteps

import com.dotdrives.design.DotButtonVariant
import com.dotdrives.design.DotTone
import com.dotdrives.model.OrgMember
import com.dotdrives.model.ReminderFilter
import com.dotdrives.model.ReminderSummary
import com.dotdrives.web.ui.DotAction
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotEmptyState
import com.dotdrives.web.ui.DotPill
import com.dotdrives.web.ui.DotSkeleton
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import react.useState
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType
import kotlin.time.Instant

external interface ReminderListProps : Props {
    var canWrite: Boolean
    var loading: Boolean
    var reminders: List<ReminderSummary>
    var filter: ReminderFilter
    var error: String?
    var members: List<OrgMember>
    var now: Instant
    var onRetry: () -> Unit
    var onComplete: (String) -> Unit
    var onSnooze: (String, Instant) -> Unit
    var onReassign: (String, String, String?) -> Unit
    var onNewReminder: () -> Unit
    var onClearFilter: () -> Unit
    var onOpenAutomationBuilder: (() -> Unit)?
}

val ReminderList = FC<ReminderListProps> { props ->
    props.error?.let { error ->
        DotBanner {
            message = error
            onRetry = props.onRetry
            className = "ns-inline-banner"
        }
    }

    val emptyKind = reminderEmptyKind(props.filter, props.reminders.isEmpty())

    when {
        props.loading && props.reminders.isEmpty() -> div {
            className = ClassName("ns-skeleton-list")
            repeat(4) { DotSkeleton { height = "64px" } }
        }
        // A load failure must never present as "you have nothing to do" -- the banner above
        // already carries the retry affordance, so the body stays quiet rather than piling a
        // second, contradictory empty state on top of it.
        props.error != null && props.reminders.isEmpty() -> Unit
        emptyKind == ReminderEmptyKind.AllDone -> AllDoneState {
            onNewReminder = if (props.canWrite) props.onNewReminder else null
            onOpenAutomationBuilder = props.onOpenAutomationBuilder
        }
        emptyKind == ReminderEmptyKind.NoFilterMatches -> DotEmptyState {
            emoji = "🔍"
            title = "Nothing matches this filter"
            body = "Other reminders exist — they're just outside this view."
            primaryAction = DotAction("Show all reminders", props.onClearFilter)
            secondaryAction = if (props.canWrite) DotAction("+ New reminder", props.onNewReminder) else null
        }
        else -> {
            groupReminders(props.reminders, props.now).forEach { group ->
                div {
                    key = group.bucket.name.unsafeCast<Key>()
                    className = ClassName("ns-section-label" + if (group.bucket == DueBucket.Overdue) " ns-section-label-overdue" else "")
                    +group.label
                }
                if (group.bucket == DueBucket.Overdue) {
                    group.reminders.forEach { reminder ->
                        OverdueCard {
                            key = reminder.id.unsafeCast<Key>()
                            canWrite = props.canWrite
                            this.reminder = reminder
                            members = props.members
                            now = props.now
                            onComplete = { props.onComplete(reminder.id) }
                            onSnooze = { dueAt -> props.onSnooze(reminder.id, dueAt) }
                            onReassign = { userId, name -> props.onReassign(reminder.id, userId, name) }
                        }
                    }
                } else {
                    div {
                        className = ClassName("ns-row-list")
                        group.reminders.forEach { reminder ->
                            WeekRow {
                                key = reminder.id.unsafeCast<Key>()
                                canWrite = props.canWrite
                            this.reminder = reminder
                                members = props.members
                                now = props.now
                                onComplete = { props.onComplete(reminder.id) }
                                onSnooze = { dueAt -> props.onSnooze(reminder.id, dueAt) }
                                onReassign = { userId, name -> props.onReassign(reminder.id, userId, name) }
                            }
                        }
                    }
                }
            }
        }
    }
}

private external interface RowProps : Props {
    var canWrite: Boolean
    var reminder: ReminderSummary
    var members: List<OrgMember>
    var now: Instant
    var onComplete: () -> Unit
    var onSnooze: (Instant) -> Unit
    var onReassign: (String, String?) -> Unit
}

private enum class RowPopover { None, Snooze, Reassign }

private val OverdueCard = FC<RowProps> { props ->
    val reminder = props.reminder
    val (popover, setPopover) = useState(RowPopover.None)

    div {
        className = ClassName("ns-card ns-card-overdue")
        if (props.canWrite) react.dom.html.ReactHTML.button {
            type = ButtonType.buttonType
            className = ClassName("ns-card-box ns-card-box-overdue")
            ariaLabel = "Mark done: ${reminder.description}"
            onClick = { props.onComplete() }
        }
        div {
            className = ClassName("ns-card-body")
            div { className = ClassName("ns-card-text"); +reminder.description }
            div {
                className = ClassName("ns-card-meta")
                +"${reminder.subjectContext} · "
                span { className = ClassName("ns-card-meta-due"); +dueLabel(reminder.dueAt, props.now) }
            }
            if (props.canWrite) div {
                className = ClassName("ns-card-actions")
                react.dom.html.ReactHTML.button {
                    type = ButtonType.buttonType
                    className = ClassName("ns-pill-btn ns-pill-btn-primary")
                    onClick = { props.onComplete() }
                    +"Do it now"
                }
                react.dom.html.ReactHTML.button {
                    type = ButtonType.buttonType
                    className = ClassName("ns-pill-btn")
                    onClick = { setPopover(if (popover == RowPopover.Snooze) RowPopover.None else RowPopover.Snooze) }
                    +"Snooze ▾"
                }
                react.dom.html.ReactHTML.button {
                    type = ButtonType.buttonType
                    className = ClassName("ns-pill-btn")
                    onClick = { setPopover(if (popover == RowPopover.Reassign) RowPopover.None else RowPopover.Reassign) }
                    +"Reassign"
                }
                if (popover == RowPopover.Snooze) {
                    SnoozePopover {
                        now = props.now
                        alignLeft = true
                        onPick = { dueAt -> props.onSnooze(dueAt); setPopover(RowPopover.None) }
                        onClose = { setPopover(RowPopover.None) }
                    }
                }
                if (popover == RowPopover.Reassign) {
                    ReassignPopover {
                        members = props.members
                        currentUserId = reminder.assignedToUserId
                        alignLeft = true
                        onPick = { userId, name -> props.onReassign(userId, name); setPopover(RowPopover.None) }
                    }
                }
            }
        }
    }
}

private val WeekRow = FC<RowProps> { props ->
    val reminder = props.reminder
    val (popover, setPopover) = useState(RowPopover.None)

    div {
        className = ClassName("ns-row")
        if (props.canWrite) react.dom.html.ReactHTML.button {
            type = ButtonType.buttonType
            className = ClassName("ns-card-box")
            ariaLabel = "Mark done: ${reminder.description}"
            onClick = { props.onComplete() }
        }
        div {
            className = ClassName("ns-row-body")
            div { className = ClassName("ns-row-text"); +reminder.description }
            div { className = ClassName("ns-row-meta"); +(autoContextLabel(reminder.ruleName) ?: reminder.subjectContext) }
        }
        div {
            className = ClassName("ns-row-controls")
            if (reminder.isAuto) {
                DotPill { text = "⚡ AUTO"; tone = DotTone.Automation }
            }
            recurrenceLabel(reminder.recurrence)?.let { label ->
                DotPill { text = "↻ $label"; tone = DotTone.Info }
            }
            span {
                className = ClassName("ns-row-due" + if (isDueToneUrgent(reminder.dueAt, props.now)) " ns-row-due-urgent" else "")
                +dueLabel(reminder.dueAt, props.now)
            }
            if (props.canWrite) react.dom.html.ReactHTML.button {
                type = ButtonType.buttonType
                className = ClassName("ns-row-more")
                ariaLabel = "More actions"
                onClick = { setPopover(if (popover == RowPopover.None) RowPopover.Snooze else RowPopover.None) }
                +"···"
            }
        }
        if (popover == RowPopover.Snooze) {
            SnoozePopover {
                now = props.now
                onPick = { dueAt -> props.onSnooze(dueAt); setPopover(RowPopover.None) }
                onClose = { setPopover(RowPopover.None) }
                extraItem = "👤 Reassign" to { setPopover(RowPopover.Reassign) }
            }
        }
        if (popover == RowPopover.Reassign) {
            ReassignPopover {
                members = props.members
                currentUserId = reminder.assignedToUserId
                onPick = { userId, name -> props.onReassign(userId, name); setPopover(RowPopover.None) }
            }
        }
    }
}

// Not private: reused by NewReminderDialog.kt for the "assigned to" picker.
external interface ReassignPopoverProps : Props {
    var members: List<OrgMember>
    var currentUserId: String
    var alignLeft: Boolean?
    var onPick: (String, String?) -> Unit
}

val ReassignPopover = FC<ReassignPopoverProps> { props ->
    div {
        className = ClassName("ns-popover" + if (props.alignLeft == true) " ns-popover-left" else "")
        if (props.members.isEmpty()) {
            div { className = ClassName("ns-popover-item-meta"); +"No teammates yet" }
        }
        props.members.forEach { member ->
            react.dom.html.ReactHTML.button {
                key = member.userId.unsafeCast<Key>()
                type = ButtonType.buttonType
                className = ClassName(
                    "ns-popover-item" + if (member.userId == props.currentUserId) " ns-popover-item-active" else "",
                )
                onClick = { props.onPick(member.userId, member.displayName) }
                +member.displayName
            }
        }
    }
}

private external interface AllDoneStateProps : Props {
    var onNewReminder: (() -> Unit)?
    var onOpenAutomationBuilder: (() -> Unit)?
}

/** "Inbox zero, fundraiser edition" (`portal-next-steps.dc.html`). The lapsed-giver
 * suggestion chips shown in the design need a lapsed-donor query this module's schema
 * section doesn't provide (that's the givers module's `lapsed-donor-alert` territory) --
 * omitted rather than fabricated; see the phase report. In their place, the second action the
 * standing empty-state constraint asks for points somewhere real instead: setting up an
 * automation rule, so the next inbox-zero moment happens with less manual work. */
private val AllDoneState = FC<AllDoneStateProps> { props ->
    div {
        className = ClassName("ns-all-done")
        div { className = ClassName("ns-all-done-emoji"); ariaHidden = true; +"🏖" }
        div { className = ClassName("ns-all-done-title"); +"Inbox zero, fundraiser edition" }
        div {
            className = ClassName("ns-all-done-copy")
            +"Every step is done. Nice work — get ahead of next week whenever you're ready."
        }
        div {
            className = ClassName("ns-all-done-actions")
            if (props.onNewReminder != null) react.dom.html.ReactHTML.button {
                type = ButtonType.buttonType
                className = ClassName("dot-button dot-button-primary")
                onClick = { props.onNewReminder?.invoke() }
                +"+ New reminder"
            }
            if (props.onOpenAutomationBuilder != null) react.dom.html.ReactHTML.button {
                type = ButtonType.buttonType
                className = ClassName("dot-button dot-button-secondary")
                onClick = { props.onOpenAutomationBuilder?.invoke() }
                +"⚡ Set up an automation rule"
            }
        }
    }
}
